import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material/material.module';
import { Organisation, Project } from '../../common/organisation';
import { AIModelChange, MODEL_CHANGE_LIMITS, ModelChangeAnswer } from '../../common/model-changes';
import { EpmAppliedChange } from '../../epm/epm-future-state';
import { AIModelChangeService, PreparedProject } from './ai-model-change.service';

export interface ModelChangeDialogData {
    organisation:Organisation
    project:Project
}

type Step = 'describe' | 'thinking' | 'questions' | 'confirm' | 'applying' | 'done'

const EXAMPLES = [
    "Add two people to Accounts Payable",
    "Replace Xero with NetSuite in every model",
    "Automate Key invoice details with an OCR system, saving 70% of the time",
]

// changes a project's models from a description. AI asks about anything unclear, the user confirms the changes it
// proposes, and the changes made are summarised at the end. Closes with true when the project changed
@Component({
    selector: 'app-model-change-dialog',
    templateUrl: './model-change-dialog.html',
    styleUrls: ['./model-change-dialog.scss'],
    standalone: true,
    imports: [MaterialModule]
})
export class ModelChangeDialogComponent implements OnInit {
    examples = EXAMPLES
    maxDescription = MODEL_CHANGE_LIMITS.description
    maxAnswer = MODEL_CHANGE_LIMITS.answer

    step:Step = 'describe'
    description = ""
    answers:ModelChangeAnswer[] = []        // to every question asked so far
    questions:{question:string, answer:string}[] = []   // the questions being asked now
    summary = ""
    changes:EpmAppliedChange[] = []
    proposed:AIModelChange[] = []           // waiting for the user to confirm them
    notPossible:EpmAppliedChange[] = []     // what AI proposed that can't be made, with why
    error = ""

    private prepared:Promise<PreparedProject>|undefined
    private changed = false

    constructor(@Inject(MAT_DIALOG_DATA) public data:ModelChangeDialogData, private service:AIModelChangeService,
        private dialogRef:MatDialogRef<ModelChangeDialogComponent, boolean>) {}

    static open(dialog:MatDialog, data:ModelChangeDialogData) {
        return dialog.open<ModelChangeDialogComponent, ModelChangeDialogData, boolean>(ModelChangeDialogComponent, {
            data, width: '640px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 64px)', autoFocus: 'textarea', disableClose: true,
        })
    }

    ngOnInit() {
        // the project is read while the user writes
        this.prepared = this.service.prepare(this.data.organisation, this.data.project)
        this.prepared.catch(err => console.error("Unable to read the project for AI", err))
    }

    get busy() {
        return this.step === 'thinking' || this.step === 'applying'
    }

    get canContinue() {
        if (this.step === 'describe') return this.description.trim().length > 0
        if (this.step === 'questions') return this.questions.every(q => q.answer.trim().length > 0)
        return false
    }

    useExample(example:string) {
        this.description = example
    }

    // sends the description, with any answers, to AI. If it's clear, the changes are shown to confirm; otherwise AI asks more
    async continue() {
        if (!this.canContinue) return
        if (this.step === 'questions') this.answers = [...this.answers, ...this.questions.map(q => ({ question: q.question, answer: q.answer.trim() }))]
        this.step = 'thinking'
        this.error = ""
        try {
            const prepared = await this.prepared!
            const response = await this.service.ask(this.data.organisation, this.data.project, prepared, this.description.trim(), this.answers)
            this.notPossible = response.rejected.map(r => ({ description: r.description, applied: false, reason: r.reason }))
            if (!response.ready) {
                this.questions = (response.questions.length ? response.questions : ["Can you say more about what should change?"]).map(question => ({ question, answer: "" }))
                this.step = 'questions'
                return
            }
            this.proposed = response.changes
            this.summary = response.summary
            this.step = 'confirm'
        }catch(err:any) {
            console.error("Unable to work out the changes with AI", err)
            this.error = "Something went wrong, so nothing was changed. Please try again."
            this.step = this.questions.length ? 'questions' : 'describe'
        }
    }

    // makes the changes the user confirmed
    async makeChanges() {
        if (this.step !== 'confirm' || !this.proposed.length) return
        this.step = 'applying'
        this.error = ""
        try {
            const applied = await this.service.apply(this.data.organisation, this.data.project, await this.prepared!, this.proposed)
            this.changed = this.changed || applied.some(c => c.applied)
            this.changes = [...applied, ...this.notPossible]
            this.step = 'done'
        }catch(err:any) {
            console.error("Unable to change the project with AI", err)
            // some may have been saved, so the page is reloaded when this closes
            this.changed = true
            this.error = "The changes couldn't all be saved. Please check the project before trying again."
            this.step = 'confirm'
        }
    }

    // back to the description, to reword it. The questions are asked again if they're still needed
    editDescription() {
        this.answers = []
        this.questions = []
        this.proposed = []
        this.notPossible = []
        this.summary = ""
        this.error = ""
        this.step = 'describe'
    }

    // starts again with a new description, keeping the project read
    another() {
        this.description = ""
        this.answers = []
        this.questions = []
        this.summary = ""
        this.changes = []
        this.proposed = []
        this.notPossible = []
        this.error = ""
        this.step = 'describe'
    }

    get madeCount() {
        return this.changes.filter(c => c.applied).length
    }

    close() {
        this.dialogRef.close(this.changed)
    }
}
